import { useState, useEffect, useMemo, useCallback } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';

type Props = {
	contentType: string;
	editorActions: EditorActions;
	isUnpublishedDraft: boolean;
	editorType?: string;
	initialShouldFocusEditor?: boolean;
};

export function useEditorTitleFocus({
	isUnpublishedDraft,
	contentType,
	editorActions,
	editorType,
	initialShouldFocusEditor = true,
}: Props): {
	isTitleFocused: boolean | null;
	shouldFocusEditor: boolean;
	debounceTitleChangeUpdates: boolean;
	focusEditor: () => void;
	onFocusChanged: (_isTitleFocused: boolean) => void;
} {
	// TODO: replace some of these arbitrary state values from graphQL query
	/**
	 * @todo CTE-268 typing in title is slow with in-editor templates (long term solution)
	 * For now, we are solving this by debouncing calls to the onTitleChange function inside EditorTitle
	 * Debouncing has some drawbacks for collaborators, so we only want this optimization when
	 * the template panel could be open
	 */
	const debounceTitleChangeUpdates = isUnpublishedDraft && contentType === 'page';
	const isTemplateEditor = editorType === 'template';

	const [isTitleFocused, setIsTitleFocused] = useState<boolean | null>(null);
	const [shouldFocusEditor, setShouldFocusEditor] = useState<boolean>(initialShouldFocusEditor);

	// This function is only called when "tab" or "enter" is pressed when focused in the title
	const focusEditor = useCallback(() => {
		setIsTitleFocused(false);
		setShouldFocusEditor(true);
	}, []);

	const onFocusChanged = useCallback((_isTitleFocused: boolean) => {
		setIsTitleFocused(_isTitleFocused);
		// we disable editor when title is focused
		// re-enabling editor leads to force focus set on it
		// this leads to focus bugs when clicking on other inputs
		// so we explicitly disable editor focus here
		setShouldFocusEditor(false);
	}, []);

	useEffect(() => {
		// focus editor ONLY when forced to.
		// Losing title focus should NOT require forcing an editor focus
		// (in the case of backtabbing or clicking on a different button)
		// TODO: CTE-2423 remove editorType check once Confluence adopts editor version Rambo (with media focus bug fix)
		if (!isTitleFocused && shouldFocusEditor && !isTemplateEditor) {
			editorActions.focus({ scrollIntoView: false });
		}
	}, [editorActions, isTitleFocused, shouldFocusEditor, isTemplateEditor]);

	return useMemo(
		() => ({
			isTitleFocused,
			shouldFocusEditor,
			debounceTitleChangeUpdates,
			focusEditor,
			onFocusChanged,
		}),
		[isTitleFocused, shouldFocusEditor, debounceTitleChangeUpdates, focusEditor, onFocusChanged],
	);
}
